import React, { useState, useRef, useEffect } from "react";
import { Checkbox, CircularProgress, Popover, Tooltip, Radio } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import "./CompactCheckboxDropdown.css";

export interface Option {
  label: string;
  value: string;
}

interface CompactCheckboxDropdownProps {
  label: string | React.ReactNode;
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  width?: number | string;
  dropdownWidth?: number | string;
  searchText?: string;
  onSearchChange?: (value: string) => void;
  enableSelectAll?: boolean;
  selectAllLabel?: string;
  multiSelect?: boolean; // New prop: true for multi-select (default), false for single-select
}

const CompactCheckboxDropdown: React.FC<CompactCheckboxDropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  onOpen,
  onClose,
  loading = false,
  disabled = false,
  placeholder = "Select options",
  width,
  dropdownWidth,
  searchText,
  onSearchChange,
  enableSelectAll = false,
  selectAllLabel = "Select all",
  multiSelect = true // Default to true for backward compatibility
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [internalSearchText, setInternalSearchText] = useState("");
  const labelRef = useRef<HTMLDivElement>(null);
  const prevSearchTextRef = useRef<string | undefined>(searchText);

  const isOpen = Boolean(anchorEl);
  const isSearchControlled = typeof searchText === "string";
  const effectiveSearchText = isSearchControlled ? searchText : internalSearchText;

  useEffect(() => {
    const prevSearchText = prevSearchTextRef.current;
    prevSearchTextRef.current = searchText;
    if (prevSearchText && prevSearchText.length > 0 && (!searchText || searchText === "")) {
      setInternalSearchText("");
    } else if (isSearchControlled && (!searchText || searchText === "")) {
      setInternalSearchText("");
    }
  }, [isSearchControlled, searchText]);

  const triggerWidthStyle =
    width !== undefined
      ? typeof width === "number"
        ? { width: `${width}px` }
        : { width }
      : undefined;

  const resolvePopoverWidth = () => {
    // Prioritize dropdownWidth if provided
    if (dropdownWidth !== undefined) {
      return typeof dropdownWidth === "number" ? dropdownWidth : dropdownWidth;
    }
    // Fall back to width prop if provided
    if (width !== undefined) {
      return typeof width === "number" ? width : width;
    }
    // Finally, use trigger's width or default
    return labelRef.current?.offsetWidth || 300;
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    if (onOpen) {
      onOpen();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (!isSearchControlled) {
      setInternalSearchText("");
    }
    if (onSearchChange) {
      onSearchChange("");
    }
    if (onClose) {
      onClose();
    }
  };

  const handleToggle = (value: string) => {
    if (multiSelect) {
      // Multi-select mode: toggle the value
      const newSelected = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onChange(newSelected);
    } else {
      // Single-select mode: replace the selection
      onChange(selectedValues.includes(value) ? [] : [value]);
      // Close dropdown after selection in single-select mode
      setTimeout(() => {
        handleClose();
      }, 100);
    }
  };

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(effectiveSearchText.toLowerCase())
  );

  const allOptionValues = options.map(option => option.value);
  const isAllSelected =
    allOptionValues.length > 0 &&
    allOptionValues.every(value => selectedValues.includes(value));

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(allOptionValues);
    }
  };

  const handleSearchInputChange = (value: string) => {
    if (!isSearchControlled) {
      setInternalSearchText(value);
    }
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const displayText = () => {
    if (selectedValues.length === 0) {
      return <span className="compact-dropdown-placeholder"></span>;
    }
    if (multiSelect) {
      return (
        <span className="compact-dropdown-selected">
          {selectedValues.length} selected
        </span>
      );
    } else {
      // Single-select mode: show the selected option label
      const selectedOption = options.find(opt => opt.value === selectedValues[0]);
      return (
        <span className="compact-dropdown-selected">
          {selectedOption?.label || selectedValues[0]}
        </span>
      );
    }
  };

  const hasSelections = selectedValues.length > 0;

  return (
    <>
      <div
        ref={labelRef}
        className={`compact-dropdown-trigger ${disabled ? "disabled" : ""} ${hasSelections ? "has-selections" : ""}`}
        onClick={handleClick}
        style={triggerWidthStyle}
      >
        {hasSelections && (
          <Tooltip title={typeof label === "string" ? label : ""} arrow>
            <span className="compact-dropdown-floating-label">{label}</span>
          </Tooltip>
        )}
        <div className="compact-dropdown-label-wrapper">
          {!hasSelections && (
            <Tooltip title={typeof label === "string" ? label : ""} arrow>
              <span className="compact-dropdown-label">{label}</span>
            </Tooltip>
          )}
            <div className="compact-dropdown-value-display">{displayText()}</div>
        </div>
        <div className="compact-dropdown-arrow">
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </div>
      </div>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            className: "compact-dropdown-paper",
            style: {
              width: resolvePopoverWidth(),
              marginTop: 4,
            },
          },
        }}
      >
        <div className="compact-dropdown-container">
          {/* Search Bar */}
          <div className="compact-dropdown-search">
            <SearchIcon className="compact-dropdown-search-icon" />
            <input
              type="text"
              className="compact-dropdown-search-input"
              placeholder="Search..."
              value={effectiveSearchText}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              autoFocus
            />
          </div>

          {enableSelectAll && multiSelect && options.length > 0 && (
            <div
              className="compact-dropdown-select-all"
              onClick={handleSelectAllToggle}
            >
              <Checkbox
                checked={isAllSelected}
                indeterminate={
                  selectedValues.length > 0 && !isAllSelected
                }
                size="small"
                sx={{
                  padding: "4px",
                  "&.Mui-checked": {
                    color: "#3B82F6",
                  },
                }}
              />
              <span>{selectAllLabel}</span>
            </div>
          )}

          {/* Options List */}
          <div className="compact-dropdown-options">
            {loading ? (
              <div className="compact-dropdown-loading">
                <CircularProgress size={24} />
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="compact-dropdown-empty">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className="compact-dropdown-option"
                  onClick={() => handleToggle(option.value)}
                >
                  {multiSelect ? (
                    <Checkbox
                      checked={selectedValues.includes(option.value)}
                      size="small"
                      sx={{
                        padding: "4px",
                        "&.Mui-checked": {
                          color: "#3B82F6",
                        },
                      }}
                    />
                  ) : (
                    <Radio
                      checked={selectedValues.includes(option.value)}
                      size="small"
                      sx={{
                        padding: "4px",
                        "&.Mui-checked": {
                          color: "#3B82F6",
                        },
                      }}
                    />
                  )}
                  <span className="compact-dropdown-option-label">
                    {option.label}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {multiSelect && selectedValues.length > 0 && (
            <div className="compact-dropdown-footer">
              {selectedValues.length} item{selectedValues.length !== 1 ? "s" : ""}{" "}
              selected
            </div>
          )}
        </div>
      </Popover>
    </>
  );
};

export default CompactCheckboxDropdown;

